import { useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCollectCardGameRewardsMutation,
  useOpenCardsCellMutation,
} from '@/api/queries/game';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { HeaderAnimationContext } from '@/context/HeaderAnimationContext';
import { setTabBarHighlight } from '@/redux/reducers/appReducer';
import { setActiveBoard } from '@/redux/reducers/diceGameReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../context';

interface UseCollectRewardsReturnType {
  isLoading: boolean;
  collectRewards: () => void;
  reset: () => void;
}

const useCardsGameCollectRewards = (args?: {
  withoutReturnToDiceBoard?: boolean;
}): UseCollectRewardsReturnType => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setIsFlyingCardsReward } = useContext(DiceGameContext);
  const { setCashBalanceScale } = useContext(HeaderAnimationContext);
  const kind = useAppSelector((state) => state.diceGame.miniGame?.type);
  const skipAnimation = useAppSelector((state) => state.diceGame.skipAnimation);
  const [, { reset }] = useOpenCardsCellMutation({
    fixedCacheKey: 'cards-mini-game-open-cell',
  });
  const [collectRewardsRequest, { isLoading }] =
    useCollectCardGameRewardsMutation({
      fixedCacheKey: 'cached-collect-card-game-rewards',
    });
  const timoutRef = useRef<NodeJS.Timeout | null>(null);

  const collectRewards = useCallback((): void => {
    collectRewardsRequest()
      .unwrap()
      .then((data) => {
        if (skipAnimation) {
          if (!args?.withoutReturnToDiceBoard) {
            dispatch(setActiveBoard('dice'));
          }

          return;
        }

        if (
          (kind === 'Material' || kind === 'Product' || kind === 'Resource') &&
          Object.values(data.totalReward).some((reward) => reward > 0)
        ) {
          timoutRef.current = setTimeout(
            () => dispatch(setTabBarHighlight('Stock')),
            1000,
          );
          setIsFlyingCardsReward(kind);

          return;
        }

        if (
          kind === 'cash' &&
          !!data.totalReward.cash &&
          data.totalReward.cash > 0
        ) {
          setIsFlyingCardsReward('cash');
          timoutRef.current = setTimeout(() => setCashBalanceScale(true), 300);

          return;
        }

        if (!args?.withoutReturnToDiceBoard) {
          dispatch(setActiveBoard('dice'));
        }
      })
      .catch((error) => {
        console.error(error);
        showToast(t('Could not collect the reward, please try again later'));
      })
      .finally(reset);
  }, [collectRewardsRequest, t]);

  useEffect(() => {
    return (): void => {
      if (timoutRef.current) {
        clearTimeout(timoutRef.current);
      }
    };
  }, []);

  return { collectRewards, isLoading, reset };
};

export { useCardsGameCollectRewards };
